import type { ArbiterQuery, ArbiterResponse } from './types';

/**
 * Deterministic Arbiter Service for CFO Command Centre
 * Uses keyword-based pattern matching - NO LLMs
 */

const responses: Record<string, ArbiterResponse> = {
  'cost to income': {
    content: `The Cost-to-Income (C/I) ratio currently stands at 52.3%, which is above the target of 50.0%. This represents an 8% increase YoY, driven primarily by elevated technology spend (£392M vs £385M budget) and regulatory compliance costs.

Key drivers of the deterioration:
• Technology investments: +£45M above plan
• Regulatory compliance: +£18M increase
• Inflation impact on staff costs: +£22M

Recommendations:
1. Implement zero-based budgeting review for technology programs
2. Explore shared services consolidation to reduce overhead
3. Target 50% C/I ratio by Q2 2025 through efficiency initiatives

Current efficiency programs are expected to deliver £42M savings this year.`,
    confidence: 0.95,
    suggestions: ['efficiency initiatives', 'technology spend', 'budget variance'],
  },

  liquidity: {
    content: `UK Bank plc maintains a strong liquidity position with a Liquidity Coverage Ratio (LCR) of 142%, well above the 100% regulatory minimum.

Current metrics:
• Total HQLA: £42.5B
• LCR: 142% (improving trend)
• NSFR: 118% (stable)
• Wholesale Funding: £18.2B

Stress testing shows resilience:
• Base case LCR: 149%
• Combined stress: 112%
• Severe adverse: 104% (still above minimum)

The liquidity buffer provides £14.0B surplus above stressed requirements. Recent wholesale funding issuances have extended the maturity profile, with weighted average maturity of 18 months.`,
    confidence: 0.98,
    suggestions: ['stress testing', 'funding composition', 'treasury position'],
  },

  capital: {
    content: `Capital position remains robust with CET1 ratio at 13.2%, providing a 6.2% surplus above the 7.0% minimum requirement.

Capital metrics:
• CET1 Ratio: 13.2% (stable)
• Total Capital Ratio: 17.8%
• Leverage Ratio: 5.8%
• Total RWA: £58.5B

Recent RWA movements:
• Credit growth: +£2.8B
• Model changes: -£2.8B (IRB updates)
• Net movement: -£1.7B vs prior quarter

Stress testing indicates capital adequacy across scenarios:
• Adverse scenario: 10.2% CET1
• Severe adverse: 7.8% CET1 (0.8% above minimum)

Capital distribution this year includes £285M dividends and £75M share buybacks, with £445M retained earnings to support growth.`,
    confidence: 0.97,
    suggestions: ['stress testing', 'RWA', 'dividend policy'],
  },

  budget: {
    content: `FY25 budget performance shows strong revenue delivery with some cost pressures.

Budget vs Actual Summary:
• Revenue: £2.45B vs £2.40B target (102% - £50M favorable)
• Operating Costs: £1.28B vs £1.30B target (98% - £20M favorable)
• Capital Expenditure: £285M vs £300M budget (95% - £15M under)
• Treasury Income: £142M vs £120M target (118% - £22M favorable)

Divisional variances:
• Technology: 102% of budget (£7M overspend)
• Commercial Banking: 101% of budget (£5M overspend)
• Other divisions tracking to plan

Full year forecast: £2.58B revenue target with 92% confidence. Risk factors include interest rate volatility and competitive pressure on margins.`,
    confidence: 0.96,
    suggestions: ['divisional performance', 'forecast accuracy', 'variance analysis'],
  },

  forecast: {
    content: `Financial forecasting accuracy currently at 92%, slightly below the 95% target.

Forecast Performance:
• Revenue forecast variance: ±4.2%
• Cost forecast variance: ±2.8%
• Overall accuracy: 92%
• Confidence level: Medium-High

Key challenges affecting accuracy:
• Interest rate environment volatility (±15% NII variance)
• Regulatory uncertainty
• Market conditions

FY25 Guidance:
• Base case: £2.58B revenue, £465M profit
• Adverse case: £2.48B revenue, £425M profit
• Severe case: £2.38B revenue, £385M profit

Recommendation: Implement scenario-based guidance ranges and enhance macroeconomic modeling to improve forecast confidence to 95%+ threshold.`,
    confidence: 0.94,
    suggestions: ['scenario planning', 'forecast accuracy', 'guidance'],
  },

  treasury: {
    content: `Treasury operations are performing well with stable funding and strong NII delivery.

Treasury Position Summary:
• Total Liquidity: £42.5B
• Net Interest Income: £1,285M (Q4 2024)
• Treasury Income: £142M vs £120M target (+18%)
• Wholesale Funding: £18.2B with average maturity 18 months

NII Sensitivity Analysis:
• +100bps rate shock: +£68M (+6.1%)
• -100bps rate shock: -£75M (-6.8%)
• Current hedge coverage: 65% of interest rate risk

Funding Composition:
• Customer Deposits: £68B (72%)
• Wholesale Funding: £18B (19%)
• Equity: £8.5B (9%)

The treasury hedging program effectively manages interest rate risk, though -100bps scenario exceeds risk appetite by £22M. Recommend expanding hedging program and diversifying asset mix.`,
    confidence: 0.96,
    suggestions: ['interest rate risk', 'funding strategy', 'hedging program'],
  },

  default: {
    content: `I can provide CFO-level insights on the following areas:

Financial Performance:
• Cost-to-income ratio and efficiency
• Revenue and profit analysis
• Budget vs actual performance

Treasury & Liquidity:
• Liquidity position and LCR/NSFR metrics
• Funding composition and strategy
• Interest rate sensitivity

Capital Management:
• CET1 and capital ratios
• RWA movements and optimization
• Stress testing results

Financial Planning:
• Budget performance and variances
• Forecast accuracy and scenarios
• Three-year financial plan

What specific area would you like to explore?`,
    confidence: 0.85,
    suggestions: ['cost to income', 'liquidity', 'capital', 'budget', 'forecast'],
  },
};

/**
 * Process a query and return a deterministic response
 */
export function processQuery(query: ArbiterQuery): ArbiterResponse {
  const normalizedQuery = query.query.toLowerCase().trim();

  // Exact keyword matching
  for (const [keyword, response] of Object.entries(responses)) {
    if (keyword === 'default') continue;
    if (normalizedQuery.includes(keyword)) {
      return response;
    }
  }

  // Return default response if no match
  return responses.default;
}

/**
 * Simulate async processing with delay
 */
export async function queryArbiter(query: ArbiterQuery): Promise<ArbiterResponse> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 800));

  return processQuery(query);
}
